import type { ConsentSignals } from '@/lib/consent/consent-signals';

import type { AnalyticsAdapter, CaptureOptions } from './types';

import {
  isNoisyClientException,
  shouldDropPostHogExceptionEvent,
} from './noisy-client-exceptions';
import { POSTHOG_CAPTURE_ENVIRONMENTS } from './posthog-capture-environments';
import { stripPiiKeys } from './strip-pii';

/**
 * Type alias for the lazy-imported `posthog-js` module's default export.
 * Pulled in only as a type (`import type`) so TS knows the shape without
 * forcing the actual bundle into the eagerly-loaded chunk.
 */
type PostHogSdk = typeof import('posthog-js').posthog;

interface PostHogAdapterConfig {
  apiKey: string;
  environment: string;
}

interface PostHogLoadConfig {
  apiKey: string;
  initialPersistence: 'localStorage+cookie' | 'memory';
  captureExceptions: boolean;
  optOutCapturing: boolean;
}

/**
 * Module-scoped promise so multiple adapter instances on the same page
 * cannot trigger duplicate `posthog.init` calls. The first adapter to
 * call wins on init config (apiKey, persistence, captureExceptions);
 * any subsequent adapter shares the same SDK and just awaits this
 * promise. In practice the `getOrCreateAnalyticsClient` cache means
 * only one adapter is constructed per page — this is belt-and-braces
 * against future bypasses (tests, dev hot-reload, future call sites).
 */
let postHogLoadPromise: Promise<PostHogSdk> | null = null;

function loadPostHogSdk(config: PostHogLoadConfig): Promise<PostHogSdk> {
  if (postHogLoadPromise) return postHogLoadPromise;
  postHogLoadPromise = (async () => {
    try {
      const { posthog } = await import('posthog-js');
      posthog.init(config.apiKey, {
        api_host: 'https://p.respondent.io',
        defaults: '2026-01-30',
        capture_pageview: false,
        capture_pageleave: false,
        autocapture: false,
        capture_exceptions: config.captureExceptions,
        persistence: config.initialPersistence,
        opt_out_capturing_by_default: config.optOutCapturing,
        disable_session_recording: true,
        // Drop known-unactionable $exception events (blocked ad pixels,
        // stale-tab chunk loads, cross-origin `Script error.`) before they
        // reach error tracking. Autocapture is the volume path; dashboard
        // suppression rules are not applied until after the first events.
        before_send: (event) =>
          shouldDropPostHogExceptionEvent(event) ? null : event,
        // No client-side `sampleRate` override: sampling is controlled
        // entirely from the PostHog project's dashboard setting. A
        // client-side value here would always win over the dashboard's,
        // which is exactly the control this app intentionally leaves
        // to PostHog (e.g. to record specific cohorts at a different
        // rate without a deploy).
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: { password: true },
        },
        advanced_disable_feature_flags_on_first_load: true,
      });
      return posthog;
    } catch (error) {
      // Clear the cached promise so a subsequent routed call can retry
      // the load instead of awaiting an already-rejected promise.
      postHogLoadPromise = null;
      throw error;
    }
  })();
  return postHogLoadPromise;
}

/** Test-only reset hook — clears module-scoped load state between tests. */
export function __resetPostHogAdapterModuleStateForTesting() {
  postHogLoadPromise = null;
}

/**
 * PostHog adapter that lazy-loads `posthog-js` (~150KB min+gzip) on first
 * use, keeping it out of the main bundle for sessions that never need it
 * (participant sessions, where routing excludes PostHog by default).
 *
 * ## Lifecycle
 *
 * 1. **Constructed by `createPostHogAdapter`** with the apiKey + environment.
 *    No SDK loaded yet. Listed in the routing destinations as `'posthog'`.
 *
 * 2. **First call that needs the real SDK** — capture, captureException,
 *    or identify — triggers `lazyLoadPostHog()`. The call itself is
 *    queued for replay once the SDK is ready. Subsequent calls keep
 *    queuing until the load finishes.
 *
 * 3. **Lifecycle calls (optIn, optOut, reset, toggleSessionRecording)**
 *    are NOT load-triggering. They update internal pending state so the
 *    SDK boots with the right config (consent, persistence) once it does
 *    load. Critically, this means a participant session that only ever
 *    receives lifecycle calls (via the AnalyticsClient's `fanout`) never
 *    causes posthog-js to be fetched.
 *
 * 4. **Once loaded** — pending state is applied, the queue is drained
 *    onto the real SDK, and all subsequent calls pass through directly.
 *
 * ## Environment gating
 *
 * Captures are dropped (without triggering a load) when the environment
 * isn't in `POSTHOG_CAPTURE_ENVIRONMENTS`. Local dev would otherwise
 * count against the PostHog plan. Lifecycle calls (identify, reset,
 * etc.) are unaffected by the env gate — only billable activity is.
 */
export class PostHogAdapter implements AnalyticsAdapter {
  readonly name = 'posthog' as const;

  private readonly apiKey: string;
  private readonly captureEvents: boolean;

  private sdk: PostHogSdk | null = null;
  private loadStarted = false;

  /** Calls deferred until the SDK loads. */
  private readonly queue: Array<(sdk: PostHogSdk) => void> = [];

  /**
   * Latest consent intent. Applied once the SDK loads so its `persistence`
   * config matches and any `reset` from optOut is honoured. We track only
   * the latest value (not a sequence) — consent toggles are idempotent so
   * intermediate transitions don't matter.
   */
  private pendingConsent: 'in' | 'out' | null = null;

  /**
   * Latest session-recording intent. Same idea — record the desired state,
   * apply once SDK is ready. Critically, this does NOT trigger a load:
   * `toggleSessionRecording` is the only routed method other than
   * capture/identify/captureException, and our policy is "session replay
   * piggy-backs on existing PostHog loading, never causes it on its own".
   */
  private pendingSessionRecording: 'on' | 'off' | null = null;

  constructor(config: PostHogAdapterConfig) {
    this.apiKey = config.apiKey;
    this.captureEvents = POSTHOG_CAPTURE_ENVIRONMENTS.has(config.environment);
  }

  // ── Routed methods: trigger lazy load on first call ─────────────────

  /**
   * PostHog has no first-class page primitive — pageviews are events
   * named `$pageview`. Mapping `page` → `track('$pageview', ...)` keeps
   * the convention internal to this adapter; the AnalyticsClient stays
   * agnostic. The `screenName` property keeps the page name accessible
   * alongside any caller-supplied attributes.
   */
  page(
    name: string,
    properties?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    // Spread properties first so the canonical `screenName` (route name)
    // cannot be silently clobbered by a caller-supplied attribute of the
    // same name.
    this.track('$pageview', { ...properties, screenName: name }, options);
  }

  track(
    event: string,
    properties?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    if (!this.captureEvents) return;
    const stripped = stripPiiKeys(properties);
    const apply = (sdk: PostHogSdk) =>
      sdk.capture(
        event,
        stripped,
        options?.immediate
          ? { send_instantly: true, transport: 'sendBeacon' }
          : undefined,
      );
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadPostHog();
  }

  trackException(
    error: unknown,
    additionalProperties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    if (!this.captureEvents) return;
    // Error-boundary / captureException path. Autocaptured unhandled
    // errors still go through `before_send` on init; this early return
    // also avoids lazy-loading posthog-js just to drop noise.
    if (isNoisyClientException(error)) return;
    // Note: posthog-js's `captureException(err, props)` doesn't expose a
    // transport override, so `options.immediate` can't be honoured here
    // (the SDK builds the $exception event and dispatches it via the
    // default transport). Accepted for interface conformance with GA.
    const stripped = stripPiiKeys(additionalProperties);
    const apply = (sdk: PostHogSdk) => sdk.captureException(error, stripped);
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadPostHog();
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    const apply = (sdk: PostHogSdk) => {
      sdk.identify(userId, traits);
      // Identify normally reloads flags. When capturing is opted out
      // some SDK versions skip that, and client flags still need a
      // `/flags` fetch so targeting works without analytics cookies.
      if (this.pendingConsent === 'out') {
        sdk.reloadFeatureFlags();
      }
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadPostHog();
  }

  // ── Lifecycle methods: update internal state, never trigger load ────

  reset() {
    if (this.sdk) {
      this.sdk.reset();
      // posthog-js `reset()` also clears the SDK's stored consent
      // (`consent.reset()`), which would silently resume capturing for a
      // user who opted out after init. Re-assert the current intent.
      this.applyConsentToSdk(this.sdk);
      return;
    }
    // Pre-load reset (e.g. logout before posthog-js finishes importing):
    // drop any queued capture/identify so they don't replay after the
    // import resolves and silently attribute the new anonymous session
    // to the prior identified user.
    this.queue.length = 0;
  }

  /**
   * PostHog has a single storage switch rather than per-purpose signals, so
   * it follows `analytics_storage`. The ad signals are meaningless here.
   */
  setConsent(signals: ConsentSignals) {
    if (signals.analytics_storage === 'granted') {
      this.optIn();
    } else {
      this.optOut();
    }
  }

  optIn() {
    this.pendingConsent = 'in';
    if (this.sdk) this.applyConsentToSdk(this.sdk);
  }

  optOut() {
    this.pendingConsent = 'out';
    if (this.sdk) {
      // Drop any cookie-backed identity as part of revoking consent. A
      // feature-flag identify after this re-sets distinct_id in memory only.
      this.applyConsentToSdk(this.sdk, { resetIdentity: true });
      return;
    }
    // Pre-load consent revocation: drop the queue so events captured
    // under the prior (assumed-granted) consent don't fire once the
    // SDK finishes importing. A later identify for flags is queued
    // after this and is not dropped.
    this.queue.length = 0;
  }

  toggleSessionRecording(toState: 'on' | 'off') {
    this.pendingSessionRecording = toState;
    if (this.sdk) this.applySessionRecording(this.sdk, toState);
  }

  isReady(): boolean {
    return this.sdk !== null;
  }

  // ── Internal ────────────────────────────────────────────────────────

  /**
   * Triggers the page-level lazy load (via `loadPostHogSdk`), then once
   * the SDK is ready applies this instance's pending state and drains
   * its queued calls. Safe to call multiple times per instance — only
   * the first call kicks off the wait. The underlying `posthog.init`
   * runs at most once per page regardless of how many adapter
   * instances exist.
   */
  private lazyLoadPostHog(): void {
    if (this.loadStarted || globalThis.document === undefined) return;
    this.loadStarted = true;

    const persistenceAtInit: 'localStorage+cookie' | 'memory' =
      this.pendingConsent === 'out' ? 'memory' : 'localStorage+cookie';

    void (async () => {
      try {
        const posthog = await loadPostHogSdk({
          apiKey: this.apiKey,
          initialPersistence: persistenceAtInit,
          captureExceptions: this.captureEvents,
          optOutCapturing: this.pendingConsent === 'out',
        });

        this.sdk = posthog;

        // If consent changed while the SDK was importing (e.g. optOut()
        // fired during `await import('posthog-js')`), re-apply the
        // latest intent — otherwise the SDK is stuck with whatever
        // persistence it was init'd with and the consent change is lost.
        // When nothing changed, the init config already encodes the
        // consent state, so there is nothing to re-apply.
        const persistenceNow: 'localStorage+cookie' | 'memory' =
          this.pendingConsent === 'out' ? 'memory' : 'localStorage+cookie';
        if (persistenceNow !== persistenceAtInit) {
          // Flipping to opted-out mid-import: also drop the identity the
          // SDK may have just restored from cookie/localStorage.
          this.applyConsentToSdk(posthog, {
            resetIdentity: persistenceNow === 'memory',
          });
        }

        // Apply any session-recording intent that arrived before load.
        if (this.pendingSessionRecording !== null) {
          this.applySessionRecording(posthog, this.pendingSessionRecording);
        }

        // Drain queued calls in order. Per-call try/catch so a single
        // failing entry doesn't poison the rest of the replay.
        for (const apply of this.queue) {
          try {
            apply(posthog);
          } catch (error) {
            console.error('PostHog queue drain error', error);
          }
        }
        this.queue.length = 0;
      } catch (error) {
        console.error('Failed to lazy-load PostHog', error);
        // Allow a future routed call to retry; clear the queue so we
        // don't accumulate stale events across attempts.
        this.queue.length = 0;
        this.loadStarted = false;
      }
    })();
  }

  /**
   * Single place that translates `pendingConsent` into SDK state. Every
   * consent transition (optIn/optOut, post-import re-check, post-reset
   * re-assert) goes through here so the ordering invariants below hold
   * everywhere.
   *
   * - `sdk.reset()` clears the SDK's stored consent, so on the opted-out
   *   path it must run BEFORE `opt_out_capturing()`, not after — otherwise
   *   the opt-out is wiped and capturing resumes.
   * - `opt_in_capturing()` captures a billable `$opt_in` event by default;
   *   consent is re-asserted on every provider effect run, so that must be
   *   suppressed.
   */
  private applyConsentToSdk(
    sdk: PostHogSdk,
    options: { resetIdentity?: boolean } = {},
  ) {
    if (this.pendingConsent === 'out') {
      if (options.resetIdentity) sdk.reset();
      sdk.opt_out_capturing();
      sdk.set_config({ persistence: 'memory' });
      return;
    }
    if (this.pendingConsent === 'in') {
      sdk.opt_in_capturing({ captureEventName: false });
      sdk.set_config({ persistence: 'localStorage+cookie' });
    }
  }

  private applySessionRecording(sdk: PostHogSdk, toState: 'on' | 'off') {
    const isRecording = sdk.sessionRecordingStarted();
    if (!isRecording && toState === 'on') sdk.startSessionRecording();
    else if (isRecording && toState === 'off') sdk.stopSessionRecording();
  }
}

/**
 * Factory: returns a `PostHogAdapter` when env has the required key, else
 * `null` so the provider can filter it out without knowing what counts as
 * "configured" for PostHog.
 */
export function createPostHogAdapter(env: {
  POSTHOG_API_KEY: string;
  ENVIRONMENT: string;
}): AnalyticsAdapter | null {
  if (!env.POSTHOG_API_KEY) return null;
  return new PostHogAdapter({
    apiKey: env.POSTHOG_API_KEY,
    environment: env.ENVIRONMENT,
  });
}
